



















import {
  Component,
  Prop,
  Ref,
  Model,
  Watch,
  Vue as VueDecorator,
} from "vue-property-decorator";
import { formatTime } from "@/utils/DateFormatter";
import { firestore } from "firebase/app";

@Component
export default class TimePickerComponent extends VueDecorator {
  @Model("change")
  timestamp!: firestore.Timestamp | null;

  @Prop() label!: string;

  @Prop({ default: false }) hideDetails!: boolean;
  @Prop({ default: true }) checkHoursAndMinutes!: boolean;
  @Prop({ default: false }) alwaysUpdateInternalTime!: boolean;
  @Prop({ default: false }) readonly!: boolean;
  @Prop({ default: false }) rejectEmptyInput!: boolean;
  @Prop({ default: true })
  setInitalTime!: boolean;
  @Prop({ default: false }) showClearInputOption!: boolean;

  @Ref("text-field")
  textField!: HTMLElement;

  internal: firestore.Timestamp | null = null;
  value = "";

  @Watch("timestamp", { immediate: true })
  onNewTimestamp(timestamp: firestore.Timestamp | null) {
    if (
      this.alwaysUpdateInternalTime &&
      this.internal?.toMillis() !== timestamp?.toMillis()
    ) {
      this.internal = timestamp;
      this.value = this.timestamp
        ? formatTime(this.timestamp, this.checkHoursAndMinutes)
        : "";
    } else if (!this.internal) {
      this.internal = timestamp;

      if (this.setInitalTime) {
        this.value = this.timestamp
          ? formatTime(this.timestamp, this.checkHoursAndMinutes)
          : "";
      }
    }
  }

  @Watch("value")
  onNewValue(newValue: string) {
    this.handle(newValue);
  }

  mounted() {
    if (!this.setInitalTime) {
      this.handle("");
    }
  }

  handle(input: string) {
    if (this.rejectEmptyInput && !input) {
      this.setNewDate(null);
      return;
    }

    const tokens = input.split(":");
    const adjustedDate = this.internal ? this.internal.toDate() : new Date();

    const firstToken = tokens[0];
    const secondToken = tokens[1];

    let hours = Number(firstToken);
    let minutes = Number(secondToken);

    if (isNaN(hours)) {
      this.setNewDate(null);
      return;
    }

    let didAdjustHours = false;
    let didAdjustMinutes = false;

    if (hours >= 24) {
      didAdjustHours = true;

      if (isNaN(minutes)) {
        minutes = Math.min(5, hours % 10);
        hours = Math.floor(hours / 10);
        didAdjustMinutes = true;
      } else {
        hours = 23;
      }
    }

    if (!isNaN(minutes) && minutes >= 60) {
      didAdjustMinutes = true;
      minutes = 59;
    }

    if (!isNaN(minutes)) {
      const adjustedInput =
        (didAdjustHours ? (hours < 10 ? `0${hours}` : hours) : firstToken) +
        ":" +
        (didAdjustMinutes ? minutes : secondToken);

      if (adjustedInput != input) {
        this.value = adjustedInput;
        return;
      }
    } else {
      const adjustedInput =
        (didAdjustHours ? hours : firstToken) +
        (input.endsWith(":") ? ":" : "");

      if (adjustedInput != input) {
        this.value = adjustedInput;
        return;
      }
    }

    try {
      adjustedDate.setHours(hours);

      if (isNaN(minutes)) {
        adjustedDate.setMinutes(0);
      } else {
        adjustedDate.setMinutes(minutes);
      }

      if (adjustedDate.toISOString()) {
        this.setNewDate(firestore.Timestamp.fromDate(adjustedDate));
      }
    } catch (error) {
      this.setNewDate(null);
    }
  }

  setNewDate(date: firestore.Timestamp | null) {
    this.internal = date;
    this.$emit("change", this.internal);
    this.$emit("timeChanged", this.internal);
  }

  setNewInternalDate(date: firestore.Timestamp | null) {
    if (!date) {
      this.internal = null;
      this.value = "";
    } else {
      this.internal = null;
      this.value = formatTime(date, this.checkHoursAndMinutes);
    }
  }

  get timePickerValue() {
    const date = this.internal?.toDate() ?? new Date();

    return date.toISOString();
  }

  get showClearInputIcon() {
    return this.showClearInputOption && !!this.internal;
  }

  get hoursAndMinutes() {
    const tokens = this.value.split(":");

    return {
      hours: tokens[0] ? parseInt(tokens[0]) : 0,
      minutes: tokens[1] ? parseInt(tokens[1]) : 0,
    };
  }

  onFocusLost() {
    const formattedTimestamp = this.timestamp
      ? formatTime(this.timestamp, this.checkHoursAndMinutes)
      : "";

    if (this.value !== formattedTimestamp) {
      this.value = formattedTimestamp;
    }
  }

  onClearInput() {
    this.value = "";
  }
}
